import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useFilters } from "@/lib/filters";
import { fmtRelative } from "@/lib/format";
import { num, rows, type LabelValue } from "@/lib/kpi";
import {
  BarList,
  ErrorState,
  GapList,
  KpiCard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

type ImportRow = { feed: string; status: string; processed: number; total: number; errors: number; finished_at: string | null; last_error: string | null };
type StaleFeed = { name: string; status: string; last_failure_at: string | null; last_success_at: string | null };

const STATUS_TONE: Record<string, string> = {
  success: "text-emerald-300",
  succeeded: "text-emerald-300",
  failed: "text-rose-300",
  running: "text-sky-300",
  pending: "text-slate-400",
};

export default function PimPage() {
  const { range } = useFilters();
  const q = useQuery({ queryKey: ["src", "pim", range], queryFn: () => dashApi.source("pim", range) });
  const k = q.data?.kpis;

  return (
    <div className="space-y-6">
      <PageHeader
        title="PIM / Catalogue"
        description="Product catalogue, supplier feeds and PrestaShop publishing."
        right={q.data && <span className="flex items-center gap-2"><StatusPill status={q.data.status} /><span className="text-xs text-slate-500">{fmtRelative(q.data.captured_at)}</span></span>}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
            <KpiCard label="Active products" value={num(k, "products_active")} unit="count" />
            <KpiCard label="Publishable" value={num(k, "products_publishable")} unit="count" />
            <KpiCard label="Archived" value={num(k, "products_archived")} unit="count" />
            <KpiCard label="Feeds active" value={num(k, "feeds_active")} unit="count" />
            <KpiCard label="Failed imports" value={num(k, "failed_imports")} unit="count" tone="danger" />
            <KpiCard label="Publish failures" value={num(k, "publish_failed")} unit="count" tone="danger" />
            <KpiCard label="Publish pending" value={num(k, "publish_pending")} unit="count" />
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="Import jobs by status (range)">
              <BarList rows={rows<LabelValue>(k, "import_jobs_by_status")} unit="count" empty="No import jobs in range." />
            </Panel>
            <Panel title="Catalogue structure">
              <BarList
                rows={[
                  { label: "Active products", value: num(k, "products_active") ?? 0 },
                  { label: "Brands", value: num(k, "brands_active") ?? 0 },
                  { label: "Categories", value: num(k, "categories_active") ?? 0 },
                ]}
                unit="count"
              />
            </Panel>
          </div>
          <Panel title="Recent feed imports">
            <RecentImports rows={rows<ImportRow>(k, "recent_imports")} />
          </Panel>
          <Panel title="Feeds needing attention">
            <StaleFeeds rows={rows<StaleFeed>(k, "stale_feeds")} />
          </Panel>
          {q.data && <GapList gaps={q.data.gaps} />}
        </>
      )}
    </div>
  );
}

function RecentImports({ rows }: { rows: ImportRow[] }) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-500">No imports recorded.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="pb-2 font-medium">Feed</th>
          <th className="pb-2 font-medium">Status</th>
          <th className="pb-2 text-right font-medium">Rows</th>
          <th className="pb-2 text-right font-medium">Errors</th>
          <th className="pb-2 text-right font-medium">Finished</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-500/40">
        {rows.map((r, i) => (
          <tr key={i} className="text-slate-300">
            <td className="py-2">{r.feed || "—"}</td>
            <td className={`py-2 ${STATUS_TONE[r.status] ?? "text-slate-400"}`}>{r.status}</td>
            <td className="py-2 text-right tabular-nums">{r.processed}/{r.total}</td>
            <td className="py-2 text-right tabular-nums">{r.errors}</td>
            <td className="py-2 text-right text-xs text-slate-500">{fmtRelative(r.finished_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StaleFeeds({ rows }: { rows: StaleFeed[] }) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-500">All active feeds are fresh.</p>;
  return (
    <ul className="divide-y divide-ink-500/40">
      {rows.map((r, i) => (
        <li key={i} className="flex items-center justify-between py-2 text-sm">
          <span className="text-slate-200">{r.name}</span>
          <span className={STATUS_TONE[r.status] ?? "text-slate-400"}>{r.status ?? "unknown"}</span>
          <span className="text-xs text-slate-500">last ok {fmtRelative(r.last_success_at)}</span>
        </li>
      ))}
    </ul>
  );
}
